/**
 * Dashboard mini-panels (Iter229 — redesign spec §4-§7).
 * All flags come from /api/dashboard/mini-panels — user-set Settings
 * thresholds evaluated against cached scanner data. Framed as "positions
 * matching your configured criteria", not platform advice.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Umbrella, Gauge, ListChecks, RefreshCw, Compass } from "lucide-react";
import api from "@/lib/api";
import { useIsPro } from "@/hooks/useIsPro";

const Card = ({ title, icon: Icon, iconColor, onOpen, testId, children }) => (
  <div className="border rounded-sm p-3" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }} data-testid={testId}>
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color: iconColor }} />
        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--hoip-text-secondary)" }}>{title}</span>
      </div>
      <button onClick={onOpen} className="flex items-center gap-0.5 text-[10px] hover:underline" style={{ color: "var(--hoip-text-tertiary)" }} data-testid={`${testId}-open`}>
        Full view <ArrowRight className="w-3 h-3" />
      </button>
    </div>
    {children}
  </div>
);

const SEVERITY_COLORS = { red: "#EF4444", amber: "#F59E0B", green: "#10B981" };

// PM row: rules on the left, traffic-light severity + Settings-rules status on the right.
const PmRow = ({ f }) => (
  <div className="py-1 border-b last:border-0 flex items-start gap-2" style={{ borderColor: "var(--hoip-divider)" }}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-mono-data text-[11px] font-bold" style={{ color: "var(--hoip-text-primary)" }}>{f.ticker}</span>
        <span className={`font-mono-data text-[10px] ${f.pnl_pct >= 0 ? "text-emerald-500" : "text-red-400"}`}>{f.pnl_pct >= 0 ? "+" : ""}{f.pnl_pct}%</span>
      </div>
      {f.rules.map((r, i) => (
        <div key={i} className="text-[10px] leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>· {r}</div>
      ))}
      {f.fundamentals && (
        <div className="text-[10px] leading-snug" style={{ color: "#60A5FA" }} data-testid={`pm-fundamentals-${f.ticker}`}
          title="Fundamentals context from the 24h valuation cache — descriptive, not part of your rule triggers">· {f.fundamentals}</div>
      )}
    </div>
    <div className="flex items-center gap-1 pt-0.5 shrink-0" data-testid={`pm-status-${f.ticker}`}
      title={`${f.rule_count || f.rules.length} of your Settings rules triggered — status label is mechanical (1=Watch · 2=Trim · 3+=Close), not advice`}>
      <span className="w-2 h-2 rounded-full inline-block" style={{ background: SEVERITY_COLORS[f.severity] || "#71717A" }} />
      <span className="text-[10px] font-bold" style={{ color: SEVERITY_COLORS[f.severity] || "var(--hoip-text-secondary)" }}>{f.status_label || "Watch"}</span>
    </div>
  </div>
);

// Hedge row: Hedged / Partial / Unhedged status + current value at risk (no
// floor to project against when unhedged).
const HedgeRow = ({ f }) => {
  const st = f.hedge_status || "unhedged";
  const color = st === "hedged" ? "#10B981" : st === "partial" ? "#F59E0B" : "#EF4444";
  const label = st === "hedged" ? "Hedged" : st === "partial" ? `Partial ${f.coverage_pct}%` : "Unhedged";
  return (
    <div className="py-1 border-b last:border-0 flex items-start gap-2" style={{ borderColor: "var(--hoip-divider)" }}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono-data text-[11px] font-bold" style={{ color: "var(--hoip-text-primary)" }}>{f.ticker}</span>
          <span className={`font-mono-data text-[10px] ${f.pnl_pct >= 0 ? "text-emerald-500" : "text-red-400"}`}>{f.pnl_pct >= 0 ? "+" : ""}{f.pnl_pct}%</span>
          {f.expired_protection && (
            <span className="text-[9px] px-1 rounded-sm" style={{ color: "#F59E0B", background: "rgba(245,158,11,0.10)" }}
              title="A protective put existed but has expired — it no longer counts as coverage">put expired</span>
          )}
        </div>
        {f.rules.map((r, i) => (
          <div key={i} className="text-[10px] leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>· {r}</div>
        ))}
      </div>
      <div className="flex flex-col items-end pt-0.5 shrink-0">
        <span className="text-[10px] font-bold" style={{ color }} data-testid={`hedge-status-${f.ticker}`}
          title={st === "partial" ? `Live long puts cover ${f.coverage_pct}% of shares${f.nearest_expiry ? ` (nearest expiry ${f.nearest_expiry})` : ""}` : st === "hedged" ? "Live long puts fully cover the shares" : "No live protective puts against this position"}>
          {label}
        </span>
        {f.value_at_risk != null && (
          <span className="text-[9px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}
            title="Current market value of the position — no protective floor exists to project against"
            data-testid={`hedge-var-${f.ticker}`}>at risk ${f.value_at_risk.toLocaleString()}</span>
        )}
      </div>
    </div>
  );
};

// Hedged position row: two fixed projection anchors — outcome at current
// price, and the guaranteed floor at the protecting put strike (net of cost).
const HedgedRow = ({ h }) => {
  const fmt = (v) => `${v >= 0 ? "+" : "−"}$${Math.abs(v).toLocaleString()}`;
  const expShort = h.nearest_expiry ? h.nearest_expiry.slice(5) : null;
  return (
    <div className="py-1 border-b last:border-0 flex items-start gap-2" style={{ borderColor: "var(--hoip-divider)" }} data-testid={`hedged-row-${h.ticker}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono-data text-[11px] font-bold" style={{ color: "var(--hoip-text-primary)" }}>{h.ticker}</span>
          <span className="text-[9px] px-1 rounded-sm font-bold" style={{ color: "#10B981", background: "rgba(16,185,129,0.10)" }}>Hedged {h.coverage_pct}%</span>
        </div>
        {h.current_pnl != null && (
          <div className="text-[10px] leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>
            · now: <span className={h.current_pnl >= 0 ? "text-emerald-500" : "text-red-400"}>{fmt(h.current_pnl)}</span> unrealized
          </div>
        )}
        {h.floor_pnl != null && (
          <div className="text-[10px] leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}
            title={`Worst case if held to the put's expiry: shares put at $${h.strike} minus put cost $${h.put_cost?.toLocaleString()} — the long put floors the downside`}>
            · floor @ ${h.strike}{expShort ? ` by ${expShort}` : ""}: <span className={h.floor_pnl >= 0 ? "text-emerald-500" : "text-red-400"}>{fmt(h.floor_pnl)}</span> net, protected by long put
          </div>
        )}
      </div>
      <span className="text-[10px] font-bold pt-0.5 shrink-0" style={{ color: "#10B981" }}>Hedged</span>
    </div>
  );
};

export const PositionMgmtMini = ({ data }) => {
  const navigate = useNavigate();
  const [view, setView] = useState("equities");
  const flags = data?.pm_flags || [];
  const income = data?.income_flags || [];
  const ccs = data?.cc_flags || [];
  const strategyLabel = (s) => (s || "").replace(/_/g, " ");
  return (
    <Card title="Position Management" icon={ListChecks} iconColor="#3B82F6" onOpen={() => navigate("/position-management")} testId="pm-mini-panel">
      <div className="flex rounded-sm border overflow-hidden w-fit mb-1.5" style={{ borderColor: "var(--hoip-border)" }} data-testid="pm-mini-view-toggle">
        {[["equities", `Equities${flags.length ? ` (${flags.length})` : ""}`],
          ["options", `Live Options${income.length ? ` (${income.length})` : ""}`],
          ["cc", `Possible CCs${ccs.length ? ` (${ccs.length})` : ""}`]].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} className="text-[9px] px-2 py-0.5 font-bold transition-colors"
            style={{ background: view === v ? "rgba(59,130,246,0.12)" : "transparent", color: view === v ? "#3B82F6" : "var(--hoip-text-tertiary)" }}
            data-testid={`pm-mini-view-${v}`}>{l}</button>
        ))}
      </div>
      {!data ? <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Loading…</span> : (
        <div className="overflow-y-auto pr-1" style={{ maxHeight: 148 }} data-testid="pm-mini-scroll">
          {view === "equities" && (flags.length === 0
            ? <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>No positions match your configured criteria.</span>
            : flags.map(f => <PmRow key={f.ticker} f={f} />))}
          {view === "options" && (income.length === 0
            ? <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>No open option legs in the Trade Log.</span>
            : income.map((r, i) => (
              <div key={`${r.ticker}-${r.expiry}-${i}`} className="py-1 border-b last:border-0 flex items-center gap-2" style={{ borderColor: "var(--hoip-divider)" }}
                data-testid={`pm-mini-income-${r.ticker}-${i}`}>
                <span className="font-mono-data text-[11px] font-bold w-12 shrink-0" style={{ color: "var(--hoip-text-primary)" }}>{r.ticker}</span>
                <span className="text-[9px] uppercase flex-1 min-w-0 truncate" style={{ color: "var(--hoip-text-tertiary)" }}>
                  {strategyLabel(r.strategy)}{r.strike != null ? ` $${r.strike}` : ""}{r.qty > 1 ? ` ×${r.qty}` : ""}
                </span>
                <span className="font-mono-data text-[10px] shrink-0" style={{ color: r.gamma_window ? "#F87171" : "var(--hoip-text-secondary)" }}
                  title={r.gamma_window ? `Inside the gamma-risk window (≤ ${data?.thresholds?.dte_threshold || 21}d — your DTE threshold)` : "Days to expiration"}>
                  {r.dte != null ? `${r.dte}d` : "—"}
                </span>
                {r.gamma_window && <span className="text-[8px] font-bold px-1 py-0.5 rounded-sm shrink-0" style={{ background: "rgba(239,68,68,0.12)", color: "#F87171" }}>γ</span>}
              </div>
            )))}
          {view === "cc" && (ccs.length === 0
            ? <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>No CC-favorable holdings — needs ≥100 shares, no open covered call, Volatile/Quiet regime and IV Rank above your threshold.</span>
            : ccs.map(c => (
              <div key={c.ticker} className="py-1 border-b last:border-0 flex items-center gap-2" style={{ borderColor: "var(--hoip-divider)" }}
                data-testid={`pm-mini-cc-${c.ticker}`}
                title={`${c.regime} regime · ${c.iv_rank_source === "iv-history" ? "" : "~"}IVR ${c.iv_rank} ≥ your ${c.ivr_threshold} threshold · ${c.lots} lot(s) coverable. Informational only, not a recommendation.`}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: "#10B981" }} />
                <span className="font-mono-data text-[11px] font-bold w-12 shrink-0" style={{ color: "var(--hoip-text-primary)" }}>{c.ticker}</span>
                <span className="text-[9px] flex-1 min-w-0 truncate" style={{ color: "var(--hoip-text-tertiary)" }}>{c.regime} · {c.lots} lot{c.lots !== 1 ? "s" : ""}</span>
                <span className="font-mono-data text-[10px] shrink-0" style={{ color: "#10B981" }}>{c.iv_rank_source === "iv-history" ? "" : "~"}IVR {c.iv_rank}</span>
              </div>
            )))}
        </div>
      )}
      <div className="text-[9px] italic mt-1.5" style={{ color: "var(--hoip-text-tertiary)" }}>
        {view === "cc" ? "Held names where your covered-call conditions align — informational, not advice."
          : view === "options" ? "Open option legs from the Trade Log — γ = inside your DTE threshold."
          : "Positions matching your Settings rules — informational, not advice."}
      </div>
    </Card>
  );
};

export const HedgingMini = ({ data }) => {
  const navigate = useNavigate();
  const flags = data?.hedge_flags || [];
  const hedged = data?.hedged || [];
  return (
    <Card title="Hedging" icon={Umbrella} iconColor="#EF4444" onOpen={() => navigate("/hedging")} testId="hedging-mini-panel">
      {!data ? <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Loading…</span>
        : flags.length === 0 && hedged.length === 0
          ? <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>No hedge candidates under your Settings thresholds.</span>
          : <div className="overflow-y-auto pr-1" style={{ maxHeight: 168 }} data-testid="hedging-mini-scroll">
              {flags.map(f => <HedgeRow key={f.ticker} f={f} />)}
              {hedged.map(h => <HedgedRow key={h.ticker} h={h} />)}
            </div>}
      <div className="text-[9px] italic mt-1.5" style={{ color: "var(--hoip-text-tertiary)" }}>Sorted by position size. Hedged = live long puts cover the shares; floor anchors are mechanical worst-case at the put strike. Thresholds in Settings → Risk Limits.</div>
    </Card>
  );
};

export const RiskIntelMini = ({ data }) => {
  const navigate = useNavigate();
  const { isPro } = useIsPro();
  const [sorm, setSorm] = useState(null);
  useEffect(() => {
    if (isPro === true && !sorm) {
      api.get("/portfolio/sorm").then(({ data: d }) => setSorm(d)).catch(() => {});
    }
  }, [isPro]); // eslint-disable-line react-hooks/exhaustive-deps

  const m = data?.macro || {};
  const s = sorm?.sorm || sorm?.form || null;
  const reliabilityPct = s ? 100 - (s.pf_sorm ?? s.failure_probability ?? 0) * 100 : null;
  const gaugeColor = reliabilityPct == null ? "var(--hoip-text-tertiary)" : reliabilityPct >= 90 ? "#10B981" : reliabilityPct >= 75 ? "#F59E0B" : "#EF4444";
  const ratioColor = m.ratio == null ? "var(--hoip-text-tertiary)" : m.ratio > 1 ? "#EF4444" : "#10B981";

  return (
    <Card title="Risk Intelligence" icon={Gauge} iconColor="#F59E0B" onOpen={() => navigate("/reliability")} testId="risk-mini-panel">
      {!data ? <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Loading…</span> : (
        <>
          <div className="grid grid-cols-3 gap-1 mb-1" data-testid="macro-strip">
            <div><div className="text-[8px]" style={{ color: "var(--hoip-text-tertiary)" }}>VIX</div>
              <div className="font-mono-data text-[11px] font-bold" style={{ color: (m.vix || 0) >= 25 ? "#EF4444" : (m.vix || 0) >= 18 ? "#F59E0B" : "#10B981" }}>{m.vix ?? "—"}</div></div>
            <div><div className="text-[8px]" style={{ color: "var(--hoip-text-tertiary)" }} title="VIX9D ÷ VIX — above 1.0 = near-term stress (backwardation)">VIX9D/VIX</div>
              <div className="font-mono-data text-[11px] font-bold" style={{ color: ratioColor }}>{m.ratio ?? "—"}</div></div>
            <div><div className="text-[8px]" style={{ color: "var(--hoip-text-tertiary)" }} title="Cboe S&P 500 Dispersion Index — expected 30-day dispersion among constituents">DSPX</div>
              <div className="font-mono-data text-[11px] font-bold" style={{ color: (m.dspx_pctile_6mo || 0) >= 70 ? "#F59E0B" : "var(--hoip-text-primary)" }}>
                {m.dspx ?? "—"}{m.dspx_pctile_6mo != null && <span className="text-[8px] font-normal" style={{ color: "var(--hoip-text-tertiary)" }}> p{m.dspx_pctile_6mo}</span>}
              </div></div>
          </div>
          <div className="text-[9px] leading-snug mb-1" style={{ color: "var(--hoip-text-secondary)" }} data-testid="macro-state-line">{m.state || "Macro data unavailable."}</div>
          {m.interpretation && (
            <div className="text-[9px] leading-snug mb-1.5 italic" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="macro-interpretation">{m.interpretation}</div>
          )}
          {reliabilityPct != null ? (
            <div className="flex items-center gap-2 border-t pt-1" style={{ borderColor: "var(--hoip-divider)" }}>
              <span className="font-mono-data text-sm font-bold" style={{ color: gaugeColor }}>{reliabilityPct.toFixed(0)}%</span>
              <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>floor reliability (auto)</span>
            </div>
          ) : isPro === false ? (
            <div className="text-[9px] border-t pt-1" style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }}>Floor-breach gauge is a Pro feature.</div>
          ) : null}
        </>
      )}
    </Card>
  );
};

const RRG_GROUPS = [["sectors", "Sectors"], ["megacaps", "Mega"], ["portfolio", "Mine"]];
const MAX_ROTATION_ROWS = 8;

// Compact diverging RS-Momentum bars (dashboard-sized; full RRG lives on the
// dedicated Sector Rotation page).
export const SectorRotationMini = () => {
  const navigate = useNavigate();
  const [group, setGroup] = useState("sectors");
  const [byGroup, setByGroup] = useState({});
  const [loading, setLoading] = useState(false);

  const load = async (g) => {
    setLoading(true);
    try { const { data } = await api.get(`/sector-rotation?group=${g}`); setByGroup(p => ({ ...p, [g]: data })); }
    catch (e) { console.warn("sector-rotation mini:", e?.message || e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load("sectors"); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const switchGroup = (g) => { setGroup(g); if (!byGroup[g]) load(g); };

  const sectors = byGroup[group]?.sectors || {};
  const rows = Object.entries(sectors)
    .map(([name, v]) => ({ name, etf: v.etf || name, mom: v.rs_momentum }))
    .filter(r => r.mom != null)
    .sort((a, b) => Math.abs(b.mom - 100) - Math.abs(a.mom - 100));
  const shown = rows.slice(0, MAX_ROTATION_ROWS);
  const maxDev = Math.max(1, ...shown.map(r => Math.abs(r.mom - 100)));

  return (
    <div className="border rounded-sm p-3" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }} data-testid="rotation-mini-panel">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <Compass className="w-3.5 h-3.5" style={{ color: "#3B82F6" }} />
          <span className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--hoip-text-secondary)" }}>Sector Rotation</span>
          {loading && <RefreshCw className="w-3 h-3 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />}
        </div>
        <div className="flex items-center gap-1.5">
          <div className="flex items-center border rounded-sm overflow-hidden" style={{ borderColor: "var(--hoip-border)" }}>
            {RRG_GROUPS.map(([g, label]) => (
              <button key={g} onClick={() => switchGroup(g)} className="h-6 px-2 text-[10px] font-bold"
                style={group === g ? { background: "var(--hoip-hover)", color: "var(--hoip-text-primary)" } : { color: "var(--hoip-text-tertiary)" }}
                data-testid={`rotation-mini-group-${g}`}>{label}</button>
            ))}
          </div>
          <button onClick={() => navigate("/portfolio?tab=rotation")} className="flex items-center gap-0.5 text-[10px] hover:underline" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="rotation-mini-open">
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
      {shown.length === 0 ? (
        <div className="text-[11px] py-3 text-center" style={{ color: "var(--hoip-text-tertiary)" }}>{loading ? "Computing relative strength…" : "No data."}</div>
      ) : (
        <div className="space-y-[7px] pt-1" data-testid="rotation-mini-bars">
          {shown.map(r => {
            const dev = r.mom - 100;
            const pct = Math.min(50, Math.abs(dev) / maxDev * 50);
            const color = dev >= 0.4 ? "#10B981" : dev <= -0.4 ? "#EF4444" : "#71717A";
            return (
              <div key={`${group}-${r.etf}`} className="flex items-center gap-2" title={`${r.name} (${r.etf}) — RS-Momentum ${r.mom}`}>
                <span className="font-mono-data text-[10px] w-12 truncate" style={{ color: "var(--hoip-text-secondary)" }}>{r.etf}</span>
                <div className="relative flex-1 h-[8px] rounded-sm overflow-hidden" style={{ background: "var(--hoip-hover)" }}>
                  <div className="absolute inset-y-0 w-px" style={{ left: "50%", background: "var(--hoip-border)" }} />
                  <div className="absolute inset-y-0" style={dev >= 0
                    ? { left: "50%", width: `${pct}%`, background: color }
                    : { right: "50%", width: `${pct}%`, background: color }} />
                </div>
                <span className="font-mono-data text-[10px] w-12 text-right" style={{ color }}>{r.mom?.toFixed(1)}</span>
              </div>
            );
          })}
          {rows.length > shown.length && <div className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>+{rows.length - shown.length} more in full view</div>}
        </div>
      )}
      <div className="text-[10px] italic mt-2" style={{ color: "var(--hoip-text-tertiary)" }}>RS-Momentum vs SPY · 100 = benchmark. Descriptive market internals, not a signal.</div>
    </div>
  );
};
